import React, { useState } from 'react';
import { PlayCircle, Copy, Trash2, TrendingUp, Target } from 'lucide-react';

const ScenarioModeling = ({ data, scenarios, onRunScenario, formatCurrency }) => {
  const [scenarioName, setScenarioName] = useState('');
  const [modifications, setModifications] = useState({
    revenueGrowthRate: data?.revenueGrowthRate,
    monthlyOperatingCosts: data?.monthlyOperatingCosts
  });

  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const predefinedScenarios = [
    {
      name: 'Conservative Growth',
      mods: { revenueGrowthRate: 2, monthlyOperatingCosts: parseNum(data?.monthlyOperatingCosts) * 0.9 }
    },
    {
      name: 'Aggressive Expansion',
      mods: { revenueGrowthRate: 15, monthlyOperatingCosts: parseNum(data?.monthlyOperatingCosts) * 1.3 }
    },
    {
      name: 'Cost Reduction',
      mods: { revenueGrowthRate: data?.revenueGrowthRate, monthlyOperatingCosts: parseNum(data?.monthlyOperatingCosts) * 0.7 }
    }
  ];

  const handleRunScenario = (name, mods) => {
    onRunScenario(name || scenarioName || 'Custom Scenario', mods || modifications);
    setScenarioName('');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
          <Target className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">Scenario Modeling</h3>
          <p className="text-xs text-slate-600">Compare multiple business strategies</p>
        </div>
      </div>

      {/* Quick Scenario Templates */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-slate-700 mb-3">Quick Scenarios</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {predefinedScenarios?.map((scenario, index) => (
            <button
              key={index}
              onClick={() => handleRunScenario(scenario?.name, scenario?.mods)}
              className="p-4 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition-colors text-left"
            >
              <div className="flex items-start justify-between mb-2">
                <p className="text-sm font-medium text-indigo-900">{scenario?.name}</p>
                <PlayCircle className="w-4 h-4 text-indigo-600 flex-shrink-0" />
              </div>
              <p className="text-xs text-indigo-700">
                Growth: {scenario?.mods?.revenueGrowthRate}% | 
                Costs: {((scenario?.mods?.monthlyOperatingCosts / parseNum(data?.monthlyOperatingCosts) - 1) * 100)?.toFixed(0)}%
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Scenario Builder */}
      <div className="mb-6 p-4 bg-slate-50 rounded-lg">
        <h4 className="text-sm font-medium text-slate-700 mb-3">Custom Scenario</h4>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">Scenario Name</label>
            <input
              type="text"
              value={scenarioName}
              onChange={(e) => setScenarioName(e?.target?.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="e.g., Q2 Expansion Plan"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-600 mb-1">Revenue Growth %</label>
              <input
                type="number"
                value={modifications?.revenueGrowthRate}
                onChange={(e) => setModifications(prev => ({ ...prev, revenueGrowthRate: parseFloat(e?.target?.value) }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                min="0"
                max="100"
              />
            </div>

            <div>
              <label className="block text-xs text-slate-600 mb-1">Operating Costs</label>
              <input
                type="text"
                value={modifications?.monthlyOperatingCosts}
                onChange={(e) => setModifications(prev => ({ ...prev, monthlyOperatingCosts: e?.target?.value }))}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Monthly costs"
              />
            </div>
          </div>

          <button
            onClick={() => handleRunScenario()}
            className="w-full px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
          >
            <PlayCircle className="w-4 h-4" />
            Run Scenario Analysis
          </button>
        </div>
      </div>

      {/* Scenario Results Comparison */}
      {scenarios?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-3">Scenario Comparison</h4>
          <div className="space-y-3">
            {scenarios?.slice(-3)?.map((scenario, index) => (
              <div key={index} className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{scenario?.name}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(scenario?.timestamp)?.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="p-1.5 hover:bg-slate-100 rounded transition-colors">
                      <Copy className="w-4 h-4 text-slate-600" />
                    </button>
                    <button className="p-1.5 hover:bg-red-50 rounded transition-colors">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-2 bg-slate-50 rounded">
                    <p className="text-xs text-slate-600 mb-1">Cash Runway</p>
                    <p className="text-base font-bold text-slate-900">
                      {scenario?.results?.cashRunwayMonths?.toFixed(1)} mo
                    </p>
                  </div>

                  <div className="p-2 bg-slate-50 rounded">
                    <p className="text-xs text-slate-600 mb-1">Monthly Burn</p>
                    <p className="text-base font-bold text-slate-900">
                      {formatCurrency(scenario?.results?.totalMonthlyBurn)}
                    </p>
                  </div>

                  <div className="p-2 bg-slate-50 rounded">
                    <p className="text-xs text-slate-600 mb-1">Break Even</p>
                    <p className="text-base font-bold text-slate-900">
                      {formatCurrency(scenario?.results?.breakEvenPoint)}
                    </p>
                  </div>

                  <div className="p-2 bg-green-50 rounded border border-green-200">
                    <p className="text-xs text-green-700 mb-1">Net Cash Flow</p>
                    <p className="text-base font-bold text-green-900">
                      {formatCurrency(scenario?.results?.netMonthlyCashFlow)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {scenarios?.length === 0 && (
        <div className="p-8 text-center">
          <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">No scenarios created yet</p>
          <p className="text-xs text-slate-500 mt-1">Run a scenario to compare different strategies</p>
        </div>
      )}
    </div>
  );
};

export default ScenarioModeling;